import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  FolderOpen, 
  MoreVertical, 
  Pencil, 
  Trash2,
  Calendar,
  MapPin,
  Building2,
  SortAsc,
  Filter
} from 'lucide-react';

const ProjektUebersicht = ({ onSelectProject, onNavigateToKunden }) => {
  const { projects, selectProject, deleteProject } = useProject();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'date', 'customer'

  const handleProjectClick = (project) => {
    selectProject(project.id);
    if (onSelectProject) {
      onSelectProject(project);
    }
  };

  const handleDeleteProject = (e, projectId) => {
    e.stopPropagation();
    if (window.confirm('Möchten Sie dieses Projekt wirklich löschen?')) {
      deleteProject(projectId);
    }
  };

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.address && p.address.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
      case 'customer':
        return (a.customer || '').localeCompare(b.customer || '');
      default:
        return a.name.localeCompare(b.name);
    }
  });

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Projektübersicht</h1>
            <p className="text-sm text-gray-500 mt-1">{projects.length} Projekte</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={onNavigateToKunden}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Kundenverwaltung
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Neues Projekt
            </Button>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="mt-4 flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Projekte suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <SortAsc className="w-4 h-4 mr-2" />
                Sortieren
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setSortBy('name')}>
                Nach Name
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('date')}>
                Nach Datum
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('customer')}>
                Nach Kunde
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Project Grid */}
      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sortedProjects.map((project) => (
            <Card 
              key={project.id}
              className="p-4 cursor-pointer hover:shadow-md hover:border-emerald-300 transition-all"
              onClick={() => handleProjectClick(project)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <FolderOpen className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-800 truncate">{project.name}</h3>
                    {project.customer && (
                      <p className="text-xs text-emerald-600 flex items-center gap-1 mt-0.5">
                        <Building2 className="w-3 h-3" />
                        {project.customer}
                      </p>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>
                      <Pencil className="w-4 h-4 mr-2" />
                      Bearbeiten
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={(e) => handleDeleteProject(e, project.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Löschen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="mt-3 space-y-1">
                {project.address && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{project.address}</span>
                  </p>
                )}
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(project.updatedAt || project.createdAt || Date.now()).toLocaleDateString('de-DE')}
                </p>
              </div>

              {/* Project Stats */}
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3 text-xs text-gray-500">
                <span>{project.nodes?.length || 0} Komponenten</span>
              </div>
            </Card>
          ))}

          {/* Empty State */}
          {sortedProjects.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-gray-400">
              <FolderOpen className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg">Keine Projekte gefunden</p>
              <p className="text-sm mt-1">Erstellen Sie ein neues Projekt oder ändern Sie Ihre Suche.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjektUebersicht;
